/**
 * Ablehnen und Freigeben — die beiden Entscheidungen mit Folgen.
 *
 * Beide laufen als Dialog im Fenster, nie als `window.confirm`: der
 * Browser-Dialog kann keinen Pflichtgrund abfragen, und ohne Grund ist aus der
 * GoBD-Spur spaeter nicht mehr abzulesen, warum etwas nie ein Beleg wurde
 * (§15.2, Prüfliste).
 *
 * **Rot verlangt einen Rueckruf.** Bei einer roten Warnung sind
 * `rueckruf_nummer` und `gesprochen_mit` Pflicht, und nur `admin` darf
 * freigeben (§15.1 S3). Beides prueft der Dialog vor dem Absenden — nicht
 * weil dem Server nicht zu trauen waere, sondern damit der Mensch sieht, was
 * fehlt, statt hinterher eine 422 zu bekommen. Die Regel selbst steht in
 * `lib/rechte.ts` und ist dort geprueft.
 */

import { useState } from 'react';
import { ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Auswahl, Eingabe, Feld, Textfeld } from '@/components/ui/Feld';
import { WarnungsChip } from '@/components/ui/Chip';
import { hatRoteWarnung, VERWERF_GRUND_LABEL } from '@/lib/beleg';
import { iban as ibanFormat } from '@/lib/format';
import { freigabeBereit, freigabeFehlt, istAdmin } from '@/lib/rechte';
import { useAuth } from '@/features/auth/AuthContext';
import type { DocumentResponse, Freigabe, VerwerfGrund } from '@/types';

/**
 * Dieselben fuenf Gruende wie in der Prüfliste (`VerwerfGrund` im Schema).
 *
 * Die Beschriftungen stehen seit der Prüfliste in `lib/beleg.ts` — ein Wort
 * fuer denselben Sachverhalt, an beiden Stellen aus derselben Quelle.
 */
const GRUENDE = (Object.keys(VERWERF_GRUND_LABEL) as VerwerfGrund[]).map((wert) => ({
  wert,
  text: VERWERF_GRUND_LABEL[wert],
}));

export function AblehnenDialog({
  offen,
  beleg,
  laeuft,
  onAbbrechen,
  onAblehnen,
}: {
  offen: boolean;
  beleg: DocumentResponse;
  laeuft: boolean;
  onAbbrechen: () => void;
  onAblehnen: (grund: string) => void;
}) {
  const [grund, setGrund] = useState<string>('newsletter');
  const [zusatz, setZusatz] = useState('');

  // Bei „sonstiges" ist der Freitext Pflicht — sonst stuende in der Spur nur
  // das Wort „sonstiges", und das erklaert nichts.
  const zusatzPflicht = grund === 'sonstiges';
  const bereit = !zusatzPflicht || zusatz.trim().length >= 3;

  const senden = () => {
    const text = zusatz.trim() ? `${grund}: ${zusatz.trim()}` : grund;
    onAblehnen(text);
  };

  return (
    <Dialog
      offen={offen}
      onSchliessen={onAbbrechen}
      titel="Beleg ablehnen"
      hinweis="Kein Beleg. Der Grund steht im Log und bleibt dort — die Datei selbst bleibt liegen."
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={senden} disabled={!bereit} loading={laeuft}>
            Ablehnen
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        <span className="font-medium text-slate-900">{beleg.original_name}</span>
        {beleg.supplier_name && <> · {beleg.supplier_name}</>}
      </p>

      <div className="space-y-3">
        <Feld label="Grund" pflicht>
          <Auswahl value={grund} onChange={(e) => setGrund(e.target.value)}>
            {GRUENDE.map((g) => (
              <option key={g.wert} value={g.wert}>
                {g.text}
              </option>
            ))}
          </Auswahl>
        </Feld>

        <Feld
          label={zusatzPflicht ? 'Was war es?' : 'Anmerkung (freiwillig)'}
          pflicht={zusatzPflicht}
          hinweis={zusatzPflicht ? 'Mindestens drei Zeichen.' : undefined}
        >
          <Textfeld
            rows={3}
            value={zusatz}
            onChange={(e) => setZusatz(e.target.value)}
            placeholder={zusatzPflicht ? 'Werbepost der Kammer, keine Rechnung' : ''}
          />
        </Feld>
      </div>
    </Dialog>
  );
}

/**
 * Freigeben — bei Gelb ein Klick, bei Rot ein Rückruf.
 *
 * Der Unterschied ist der ganze Punkt der Echtheitsprüfung: Gelb heisst „schau
 * hin", Rot heisst „jemand könnte gerade versuchen, euer Geld umzuleiten".
 * Deshalb verlangt Rot zwei Angaben, die man nur hat, wenn man wirklich
 * angerufen hat — die Nummer, unter der man angerufen hat, und den Namen des
 * Menschen am anderen Ende. Beide stehen danach im Log, und die IBAN-Freigabe
 * meldet sich per Benachrichtigung bei allen Admins.
 *
 * **Die Nummer aus der Mail ist die falsche.** Wer die Rechnung gefälscht hat,
 * hat auch die Nummer darunter gefälscht. Deshalb steht dieser Satz im Dialog
 * und nicht in einer Schulung.
 */
export function FreigebenDialog({
  offen,
  beleg,
  laeuft,
  onAbbrechen,
  onFreigeben,
}: {
  offen: boolean;
  beleg: DocumentResponse;
  laeuft: boolean;
  onAbbrechen: () => void;
  onFreigeben: (freigabe: Freigabe) => void;
}) {
  const { user } = useAuth();
  const [kommentar, setKommentar] = useState('');
  const [nummer, setNummer] = useState('');
  const [gesprochenMit, setGesprochenMit] = useState('');
  const [ibanUebernehmen, setIbanUebernehmen] = useState(true);

  const rot = hatRoteWarnung(beleg.warnungen);
  const rote = beleg.warnungen.filter((w) => w.stufe === 'rot');
  const gelbe = beleg.warnungen.filter((w) => w.stufe === 'gelb');
  const admin = istAdmin(user);

  const rueckruf = { nummer, gesprochenMit };
  const bereit = freigabeBereit(user, beleg.warnungen, rueckruf) && !laeuft;
  const fehlt = freigabeFehlt(user, beleg.warnungen, rueckruf);

  // Die IBAN-Übernahme steht nur zur Wahl, wenn es überhaupt um eine IBAN
  // geht. Bei „Domain täuschend ähnlich" wäre das Kästchen sinnlos und würde
  // nur den Blick von der eigentlichen Warnung wegziehen.
  const umIban = rote.some(
    (w) => w.schluessel === 'iban_geaendert' || w.schluessel === 'iban_unbestaetigt',
  );

  const senden = () =>
    onFreigeben(
      rot
        ? {
            rueckruf_nummer: nummer.trim(),
            gesprochen_mit: gesprochenMit.trim(),
            iban_bestaetigen: umIban && ibanUebernehmen,
            kommentar: kommentar.trim() || null,
          }
        : { kommentar: kommentar.trim() || null },
    );

  return (
    <Dialog
      offen={offen}
      onSchliessen={onAbbrechen}
      titel={rot ? 'Freigeben nach Rückruf' : 'Beleg freigeben'}
      hinweis={
        rot
          ? 'Eine rote Warnung. Erst anrufen, dann freigeben — beides steht danach im Log.'
          : 'Danach geht der Beleg an die Kanzlei und auf die Zu-zahlen-Liste.'
      }
      breite={rot ? 'lg' : 'md'}
      abbrechenBeiHintergrundklick={!rot}
      fussleiste={
        rot && !admin ? (
          <Button variant="outline" onClick={onAbbrechen}>
            Schließen
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
              Abbrechen
            </Button>
            <Button
              variant={rot ? 'danger' : 'primary'}
              onClick={senden}
              disabled={!bereit}
              loading={laeuft}
            >
              Freigeben
            </Button>
          </>
        )
      }
    >
      {rot ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-danger-600" />
            <div className="text-sm text-danger-700">
              <p className="font-medium">
                {umIban
                  ? 'IBAN geändert — nur nach Rückruf unter der bekannten Nummer freigeben.'
                  : 'Dieser Beleg hat eine rote Warnung.'}
              </p>
              <p className="mt-1">
                Ruft den Lieferanten unter der Nummer an, die ihr schon habt — aus dem Vertrag,
                aus einer alten Rechnung, aus dem Impressum. <strong>Nicht</strong> unter der
                Nummer, die in dieser Mail oder auf diesem Beleg steht: wer die Rechnung
                gefälscht hat, hat auch die Nummer gefälscht.
              </p>
              <ul className="mt-2 space-y-1.5">
                {rote.map((w) => (
                  <li key={w.schluessel} className="flex flex-wrap items-start gap-2">
                    <WarnungsChip warnung={w} />
                    <span className="flex-1 text-danger-700/90">{w.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {umIban && (beleg.iban || rote[0]?.belege) && (
            <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm sm:grid-cols-2">
              <div>
                <div className="text-xs font-medium text-slate-500">bekannt</div>
                <div className="font-mono text-slate-700">
                  {ibanFormat((rote[0]?.belege?.bekannt as string | undefined) ?? null)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500">auf diesem Beleg</div>
                <div className="font-mono font-medium text-danger-700">
                  {ibanFormat(
                    (rote[0]?.belege?.gefunden as string | undefined) ?? beleg.iban ?? null,
                  )}
                </div>
              </div>
            </div>
          )}

          {!admin ? (
            <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700">
              <p className="font-medium">Diese Freigabe ist Admins vorbehalten.</p>
              <p className="mt-1">
                Wer eine rote Warnung freigibt, entscheidet, wohin gezahlt wird. Bitte an Henri
                oder Anton weiterreichen — der Beleg bleibt so lange in Prüfung liegen.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Feld
                  label="Rückrufnummer"
                  pflicht
                  hinweis="Die Nummer, unter der ihr angerufen habt."
                >
                  <Eingabe
                    value={nummer}
                    onChange={(e) => setNummer(e.target.value)}
                    placeholder="0351 1234567"
                    inputMode="tel"
                  />
                </Feld>
                <Feld label="Gesprochen mit" pflicht hinweis="Wer war am Telefon?">
                  <Eingabe
                    value={gesprochenMit}
                    onChange={(e) => setGesprochenMit(e.target.value)}
                    placeholder="Frau Krause, Buchhaltung"
                  />
                </Feld>
              </div>

              {umIban && (
                <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={ibanUebernehmen}
                    onChange={(e) => setIbanUebernehmen(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    Die IBAN dieses Belegs als bestätigt übernehmen.
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Sie steht danach beim Lieferanten und erscheint auf der Zu-zahlen-Liste.
                      Alle Admins bekommen eine Benachrichtigung darüber.
                    </span>
                  </span>
                </label>
              )}

              <Feld label="Anmerkung (freiwillig)" hinweis="Steht im Log.">
                <Textfeld
                  rows={2}
                  value={kommentar}
                  onChange={(e) => setKommentar(e.target.value)}
                  placeholder="Bankwechsel bestätigt, Schreiben liegt vor"
                />
              </Feld>

              {fehlt && <p className="text-xs text-danger-600">{fehlt}</p>}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {gelbe.length > 0 ? (
            <div>
              <p className="mb-2 text-sm text-slate-600">
                {gelbe.length === 1 ? 'Diese Warnung' : 'Diese Warnungen'} bleibt im Log stehen:
              </p>
              <ul className="space-y-2">
                {gelbe.map((w) => (
                  <li key={w.schluessel} className="flex items-start gap-2 text-sm text-slate-700">
                    <WarnungsChip warnung={w} />
                    <span className="text-slate-500">{w.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-slate-600">Dieser Beleg hat keine offene Warnung.</p>
          )}

          <Feld label="Anmerkung (freiwillig)" hinweis="Steht im Log.">
            <Eingabe
              value={kommentar}
              onChange={(e) => setKommentar(e.target.value)}
              placeholder="Erste Rechnung, Lieferant angerufen"
            />
          </Feld>
        </div>
      )}
    </Dialog>
  );
}
